import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import RoundedButton from "components/RoundedButton";
import Separator from "components/Separator";
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/7-questions-for-trees-for-the-future`;

  return (
    <MainLayout>
      <NextSeo
        title="7 Questions For Trees For The Future | DAYMADE"
        description="DAYMADE is committed to contributing to environmental protection by planting trees. For each purchase on our site, a tree is planted by our charity partner Trees for the Future. This Earth Month, we have reached a milestone of 150,280 trees planted - all thanks to our amazing players! We sat down with Brandy Lellou, Director of Programs at Trees for the Future, to ask her about Trees for the Future. How do they work, where do they plant trees and how does this contribute to a healthier planet?"
        openGraph={{
          title: '7 Questions For Trees For The Future | DAYMADE',
          description:
            'DAYMADE is committed to contributing to environmental protection by planting trees. For each purchase on our site, a tree is planted by our charity partner Trees for the Future. This Earth Month, we have reached a milestone of 150,280 trees planted - all thanks to our amazing players! We sat down with Brandy Lellou, Director of Programs at Trees for the Future, to ask her about Trees for the Future. How do they work, where do they plant trees and how does this contribute to a healthier planet?',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/7-questions-for-trees-for-the-future',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Four workers for Trees for the Future crouching down holding and planting baby trees',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'trees for the future, plant trees, trees, environment, earth day, earth day 2021, plant a tree, climate,climate change,how to plant trees,sustainable brand,environmentally friendly,charity,environmental charity'
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Four workers for Trees for the Future crouching down holding and planting baby trees"
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
            7 Questions For <strong>Trees For The Future</strong>
          </h1>

          <p className="subheading">
            Celebrating Earth Day — 150,280 trees planted
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Lisa Nederveen
          </p>

          <p>
            <span className="bigFirstLetter">I</span>t is the 22nd of April and
            that can only mean one thing: it is Earth Day! Our annual reminder
            of how important it is to protect our beautiful planet. DAYMADE is
            committed to contributing to environmental protection by planting
            trees. For each purchase on our site, a tree is planted by our
            charity partner Trees for the Future. This Earth Month, we have
            reached a milestone of 150,280 trees planted - all thanks to our
            amazing players!
          </p>

          <p>
            We sat down with Brandy Lellou, Director of Programs at Trees for
            the Future, to ask her about Trees for the Future. How do they work,
            where do they plant trees and how does this contribute to a
            healthier planet?
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/brandy.jpg`}
            alt="Brandy Lellou, smiling in a woodland area wearing a Trees for the Future branded top"
            className="singleBlogImage"
          />

          <label style={{ fontWeight: 600, marginBottom: '2em' }}>
            Brandy Lellou, Director of Programs
          </label>

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            Why is planting trees so important?
          </p>

          <p>
            Trees are an effective, sustainable, low-cost solution to many of
            the world’s most pressing challenges. At Trees for the Future,
            farmers learn to incorporate trees on their farm land in what we
            call Forest Gardens. Forest Gardens are made up of thousands of
            trees and successfully restore degraded land, capture carbon,
            improve biodiversity, and provide food, income, and resources.
          </p>

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            Where are the trees planted?
          </p>

          <p>
            Each project is made up of about 250 - 600 farming families and the
            farmers work in smaller groups of about 20. Local Trees for the
            Future staff work closely with farmer groups over a four-year
            period, training them in the Forest Garden Approach and various
            agroforestry techniques. Each farmer’s Forest Garden is about an
            acre in size and typically includes more than 2,500 trees and dozens
            of different species of food crops and marketable products. You can
            find a full list of the trees, fruits, and vegetables in the Forest
            Gardens{' '}
            <a
              href="https://trees.org/whats-growing/"
              target="_blank"
            >
              here
            </a>
            .
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Before and after ariel shot of afforested field"
            className="singleBlogImage"
          />

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            How is climate change affecting Senegal?
          </p>

          <p>
            Climate change is leading to more extreme and less predictable
            weather patterns. Periods of drought, higher than average
            temperatures, and erratic rainfall are all results of a changing
            climate. For a semi-arid country like Senegal, the effects of
            climate change are felt acutely by rural farmers who are reliant on
            predictable rainfall and weather patterns. Without this, they can
            lose entire crops and their ability to provide for themselves and
            their families.
          </p>

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            How do you know how many trees are planted by Trees for the Future
            team?
          </p>

          <p>
            Our reporting system is extensive. Trees for the Future technicians
            register farmers and all trees planted through our Taroworks App
            that links with SalesForce. We have nursery periods and outplanting
            periods. We count trees when they are first outplanted and then do
            an annual evaluation to track survival rates. Our program is also
            unique in that we are not practicing mass tree planting. Each farmer
            is invested in their land and the trees they are planting on it.
          </p>

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            Can you give an example of one of the farmers who works{' '}
            <em>with</em> Trees for the Future, and how your projects have
            specifically changed his/her life and that of his/her family?
          </p>

          <p>
            Farmers enroll in the Forest Garden program. They own their land and
            Trees for the Future is providing resources and training over a
            four-year period. You can read a farmer story about{' '}
            <a
              href="https://trees.org/post/finding-opportunity-in-the-land-not-across-the-sea/"
              target="_blank"
            >
              Mamadou Fall here
            </a>
            , and one about{' '}
            <a
              href="https://trees.org/post/salma/"
              target="_blank"
            >
              Salma Mussa here
            </a>
            .
          </p>

          <div className="doubleImageContainer">
            <img
              src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
              alt="Trees for the Future farmer stood in woodland area surrounded by small growing trees"
              className="doubleBlogImage"
              style={{
                objectPosition: '80% 50%',
              }}
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt="Pair of Trees for the Future workers stood in dense woodland area"
              className="doubleBlogImage"
            />
          </div>

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            What is different about Trees for the Future's reforestation
            projects compared to other tree planting charities?
          </p>

          <p>
            Trees for the Future is taking a truly sustainable approach to tree
            planting by addressing the root cause of land degradation: land use.
            By working with farmers to improve farming practices through
            agroforestry and implement climate-smart solutions, we’re ensuring
            that trees survive to maturity, and benefit both the planet and the
            communities where they’re planted.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Before and after ariel shot of afforested field"
            className="singleBlogImage"
          />

          <p style={{ fontWeight: 700, fontSize: '1.2em' }}>
            Your mission is to end hunger and poverty by training farmers to
            regenerate their land in an environmentally friendly way. Can you
            tell something about the previous agricultural processes of these
            farmers in those areas and how the method introduced by Trees for
            the Future differs?
          </p>

          <p>
            Contemporary farming techniques center on monocrop agriculture,
            planting and harvesting just one crop. This approach to farming is
            unsustainable because it pulls all of the nutrients from the ground
            without replenishing them. It often requires more water and costly
            chemical fertilizers and pesticides. This approach to farming has
            degraded land across the globe and destroyed a third of our topsoil.
            The Forest Garden Approach emphasizes crop diversity and sustainable
            techniques that benefit both the land and the farmer. Forest Garden
            farmers are able to revitalize their land and, at the same time,
            they are able to produce more food and diverse marketable products
            to support their family.
          </p>

          <Separator style={{ margin: '2em' }} />

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px' }}
          >
            Enter Draw + Plant Tree
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
